/**
 * What kind of failure was that?
 *
 * In axios every failure arrives the same way — a thrown error — so a lapsed
 * session and an API that is still booting are indistinguishable at a bare
 * `catch (err)`. Collapsing both to a message string is how the admin console
 * came to tell a signed-out user that "the backend may still be starting up":
 * the copy was written for a cold API and then shown for an expired cookie,
 * with a Retry button that could only ever 401 again.
 *
 * The distinction is one field (`err.response.status`), so the value here is
 * not the arithmetic — it is having ONE vocabulary for it. Three surfaces mount
 * `ManagedEntityProvider` (admin, desktop operator, the marketing site) and a
 * fourth (live) shares the same api clients; each deciding for itself what a
 * 401 means is how they drift.
 *
 * Import-free on purpose: `tests/unit` is typechecked under the backend's
 * `nodenext` config, so the test imports this by relative path and anything
 * this file pulled in would have to resolve there too. Same constraint as
 * `contrastTone.ts`.
 */

export type ApiFailureKind =
    /** 401 — the server does not recognise this session. Sign in again. */
    | 'auth'
    /** 403 — a real session, refused. Signing in again changes nothing. */
    | 'forbidden'
    /** Any other 4xx — a bad request. Repeating it verbatim is pointless. */
    | 'client'
    /** 5xx — the server is up enough to answer and is failing. Worth a retry. */
    | 'server'
    /** No response at all: offline, DNS, CORS preflight, a cold API. Retry. */
    | 'network';

export interface ApiFailure {
    kind: ApiFailureKind;
    /** The HTTP status, or null when the request never got a response. */
    status: number | null;
}

/**
 * Classify a thrown axios error (or anything else) into `ApiFailure`.
 *
 * Deliberately tolerant of the argument's shape — this runs inside a `catch`,
 * where the value is `unknown` and may not be an axios error at all. Anything
 * unrecognisable reads as `network`, which is the honest answer: we have no
 * response, so we do not know.
 */
export function classifyApiFailure(err: unknown): ApiFailure {
    const status = readStatus(err);

    if (status === null) return { kind: 'network', status: null };
    if (status === 401) return { kind: 'auth', status };
    if (status === 403) return { kind: 'forbidden', status };
    if (status >= 500) return { kind: 'server', status };
    if (status >= 400) return { kind: 'client', status };

    // A 2xx/3xx that still threw (a transform blew up, a manual `throw` on an
    // envelope's `ok: false`). Not the server refusing us — treat as transient.
    return { kind: 'network', status };
}

/**
 * Is it worth issuing this request a second time?
 *
 * Only where repeating it could plausibly answer differently. A 401 retried is
 * a second 401; the retry costs the user the round trip and buys nothing.
 */
export function isRetryableApiFailure(failure: ApiFailure): boolean {
    return failure.kind === 'network' || failure.kind === 'server';
}

/**
 * Is the server refusing to recognise who we are?
 *
 * Named for the SYMPTOM, not a cause. A bare 401 does not say the session
 * expired — it can equally be a revoked token, or a cookie from another
 * Showliner environment shadowing this one on a shared parent domain (two
 * cookies of the same name, and the server reads only the first). Copy built on
 * top of this must not assert a cause it cannot see.
 *
 * Note what it does NOT include: `forbidden`. A 403 is a live session being
 * refused a specific thing, and bouncing that user to a login form asks them
 * to re-do the one step that already worked.
 */
export function isAuthFailure(failure: ApiFailure): boolean {
    return failure.kind === 'auth';
}

function readStatus(err: unknown): number | null {
    if (!err || typeof err !== 'object') return null;
    const response = (err as { response?: unknown }).response;
    if (!response || typeof response !== 'object') return null;
    const status = (response as { status?: unknown }).status;
    return typeof status === 'number' ? status : null;
}
